import { useMemo, useState } from 'react';
import { Divider, Radio, RadioGroup } from '@nextui-org/react';
import { Line } from 'react-chartjs-2';
import { ChartDataset } from 'chart.js';
import { useAppSelector } from '../../store/hooks';
import { selectMessagesWithClient } from '../../store/chat/chatSlice';
import { selectRiskOfMessages } from '../../store/assistant/assistantSlice';

const MAX_LABEL_LEN = 20;
const AXIS_TIMESTAMP = 'timestamp';
const AXIS_MESSAGE = 'message';

export default function ClientProfileTrafficLightChart() {
  const messagesWithClient = useAppSelector(selectMessagesWithClient);
  const messagesRisk = useAppSelector(selectRiskOfMessages);
  const [sentimentAxisX, setSentimentAxisX] = useState(AXIS_MESSAGE);

  const messagesFromClient = useMemo(
    () => messagesWithClient.filter((m) => m.role === 'assistant'),
    [messagesWithClient]
  );

  const sentimentMessageLabels = useMemo(() => {
    const labels = messagesFromClient.map((m) =>
      m.content.trim().length > MAX_LABEL_LEN
        ? m.content.trim().slice(0, MAX_LABEL_LEN - 3) + '...'
        : m.content.trim()
    );
    return labels;
  }, [messagesFromClient]);

  const sentimentTimestampLabels = useMemo(() => {
    const timeStamps = messagesFromClient.map((m) => Date.parse(m.createdAt));
    return timeStamps;
  }, [messagesFromClient]);

  const sentiments = useMemo(() => {
    const riskScores = messagesFromClient.map((m) => {
      const risk = messagesRisk[m.content]?.risk;

      switch (risk) {
        case 'Neutral':
          return 0;
        case 'Low':
          return 0.33;
        case 'Medium':
          return 0.66;
        case 'High':
          return 1;
        default:
          return 0;
      }
    });

    return riskScores;
  }, [messagesRisk, messagesFromClient]);

  const yLabels = {
    0: 'Neutral',
    0.33: 'Low',
    0.66: 'Medium',
    1: 'High',
  };

  const sentimentChartDatasets: ChartDataset<'line', (number | undefined)[]>[] =
    useMemo(() => {
      return [
        {
          label: 'Traffic Light score',
          data: sentiments,
          cubicInterpolationMode: 'monotone',
        },
      ];
    }, [sentiments]);

  return (
    <div className="flex flex-col gap-2">
      {sentimentAxisX === AXIS_MESSAGE && (
        <Line
          data={{
            labels: sentimentMessageLabels,
            datasets: sentimentChartDatasets,
          }}
          options={{
            scales: {
              y: {
                min: 0,
                max: 1,
                ticks: {
                  stepSize: 0.33,
                  callback: function (value) {
                    // @ts-ignore
                    return yLabels[value];
                  },
                },
              },
            },
          }}
        />
      )}
      {sentimentAxisX === AXIS_TIMESTAMP && (
        <Line
          data={{
            labels: sentimentTimestampLabels,
            datasets: sentimentChartDatasets,
          }}
          options={{
            scales: {
              x: { type: 'time', time: { tooltipFormat: 'DATE_MED' } },
              y: {
                min: 0,
                max: 1,
                ticks: {
                  stepSize: 0.33,
                  callback: function (value) {
                    // @ts-ignore
                    return yLabels[value];
                  },
                },
              },
            },
          }}
        />
      )}
      <Divider />
      <RadioGroup
        size="sm"
        orientation="horizontal"
        value={sentimentAxisX}
        onValueChange={setSentimentAxisX}
      >
        <Radio value={AXIS_MESSAGE}>Message</Radio>
        <Radio value={AXIS_TIMESTAMP}>Timestamp</Radio>
      </RadioGroup>
    </div>
  );
}
